/**
 * Audit Badges Hook
 * Provides badge counts for audit navigation items
 * Supports both demo and live mode
 */

import { useEffect, useState } from 'react';
import { demoDataService } from '../services/demo/demo-data.service';
import { auditDataService } from '../services/audit-data-service';
import { AuditStatus, ExceptionStatus, ExceptionSeverity } from '../types/asset-audit';

export interface AuditBadges {
  activeAudits: number;
  scheduledAudits: number;
  openExceptions: number;
  criticalExceptions: number;
}

// Helper function to get current data source mode from feature settings
const getDataSource = (): 'demo' | 'live' => {
  try {
    const featureSettings = localStorage.getItem('feature-settings');
    if (featureSettings) {
      const parsed = JSON.parse(featureSettings);
      const auditsModule = parsed.modules?.audits;
      if (auditsModule) {
        return auditsModule.mode === 'live' ? 'live' : 'demo';
      }
    }
  } catch (error) {
    console.error('Error reading feature settings:', error);
  }
  return 'demo';
};

export function useAuditBadges() {
  const [badges, setBadges] = useState<AuditBadges>({
    activeAudits: 0,
    scheduledAudits: 0,
    openExceptions: 0,
    criticalExceptions: 0,
  });

  useEffect(() => {
    const updateBadges = async () => {
      const dataSource = getDataSource();

      if (dataSource === 'demo') {
        // Demo mode - use demo data service
        const data = demoDataService.getData();
        
        const activeAudits = data.audits.filter(a => 
          a.status === AuditStatus.Assigned || 
          a.status === AuditStatus.InProgress || 
          a.status === AuditStatus.UnderReview ||
          a.status === AuditStatus.OnHold
        ).length;

        const scheduledAudits = data.audits.filter(a => {
          if (a.status !== AuditStatus.Scheduled) return false;
          const daysUntil = Math.floor((new Date(a.startDate!).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
          return daysUntil <= 7; // Upcoming in next 7 days
        }).length;

        const openExceptions = data.exceptions.filter(e => e.status !== ExceptionStatus.Resolved).length;
        const criticalExceptions = data.exceptions.filter(e => 
          e.status !== ExceptionStatus.Resolved && e.severity === ExceptionSeverity.Critical
        ).length;

        setBadges({
          activeAudits,
          scheduledAudits,
          openExceptions,
          criticalExceptions,
        });
      } else {
        // Live mode - fetch from API
        try {
          const [activeAuditsResult, scheduledAuditsResult, exceptionsResult] = await Promise.all([
            auditDataService.getAudits({
              statuses: [
                AuditStatus.Assigned,
                AuditStatus.InProgress,
                AuditStatus.UnderReview,
                AuditStatus.OnHold,
              ],
              pageSize: 1, // We only need the count
            }),
            auditDataService.getAudits({
              statuses: [AuditStatus.Scheduled],
              pageSize: 1000, // Need to fetch all to filter by date
            }),
            auditDataService.getExceptions({
              pageSize: 1000, // Need to fetch all to count
            }),
          ]);

          // Filter scheduled audits for upcoming in next 7 days
          const scheduledAudits = scheduledAuditsResult.items.filter(a => {
            if (!a.startDate) return false;
            const daysUntil = Math.floor((new Date(a.startDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            return daysUntil <= 7;
          }).length;

          const openExceptions = exceptionsResult.items.filter(e => e.status !== ExceptionStatus.Resolved).length;
          const criticalExceptions = exceptionsResult.items.filter(e => 
            e.status !== ExceptionStatus.Resolved && e.severity === ExceptionSeverity.Critical
          ).length;

          setBadges({
            activeAudits: activeAuditsResult.totalCount,
            scheduledAudits,
            openExceptions,
            criticalExceptions,
          });
        } catch (error) {
          console.error('Error fetching audit badges:', error);
          // Keep previous badge values on error
        }
      }
    };

    // Initial update
    updateBadges();
  }, []);

  return badges;
}
